// Proje servisi - Proje API çağrıları
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Token'ı otomatik ekle
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Proje servis fonksiyonları
export const projectService = {
  // Proje listesini al
  async getProjects(params = {}) {
    try {
      const response = await api.get('/projects', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Proje atanan kullanıcı listesini al (filtrelerle)
  async getProjectAssignees(params = {}) {
    try {
      const response = await api.get('/projects/assignees', { params });
      return response.data; // { assignees: [{id, user_fullname}...] }
    } catch (error) {
      throw error;
    }
  },

  // Proje detaylarını al
  async getProject(id) {
    try {
      const response = await api.get(`/projects/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Yeni proje oluştur
  async createProject(projectData) {
    try {
      const response = await api.post('/projects', projectData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Proje güncelle
  async updateProject(id, projectData) {
    try {
      const response = await api.put(`/projects/${id}`, projectData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Proje sil
  async deleteProject(id) {
    try {
      const response = await api.delete(`/projects/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Proje toplantısı ekle
  async addMeeting(projectId, meetingData) {
    try {
      const response = await api.post(`/projects/${projectId}/meetings`, meetingData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Toplantı güncelle
  async updateMeeting(meetingId, meetingData) {
    try {
      const response = await api.put(`/projects/meetings/${meetingId}`, meetingData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Proje notu ekle
  async addNote(projectId, noteData) {
    try {
      const response = await api.post(`/projects/${projectId}/notes`, noteData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Not güncelle
  async updateNote(noteId, noteData) {
    try {
      const response = await api.put(`/projects/notes/${noteId}`, noteData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Not sil
  async deleteNote(noteId) {
    try {
      const response = await api.delete(`/projects/notes/${noteId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Toplantı sil
  async deleteMeeting(meetingId) {
    try {
      const response = await api.delete(`/projects/meetings/${meetingId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Toplantı durumu güncelle
  async updateMeetingStatus(meetingId, status, description = '') {
    try {
      const response = await api.put(`/projects/meetings/${meetingId}/status`, { status, description });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Proje dosyası yükle
  async uploadProjectFile(projectUuid, file, description = '') {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', description);

      const response = await api.post(`/projects/${projectUuid}/files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
  ,

  // Proje dosyası sil
  async deleteProjectFile(fileId) {
    try {
      const response = await api.delete(`/projects/files/${fileId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
  ,

  // Teklif yükle
  async uploadOffer(projectUuid, file, { description = '', status = 'Hazırlandı', total = 0, profitability = 0, currency = 'TL', created_at = null } = {}) {
    try {
      const formData = new FormData();
      if (file) {
        formData.append('file', file);
      }
      formData.append('description', description);
      formData.append('status', status);
      formData.append('total', total);
      formData.append('profitability', profitability);
      formData.append('currency', currency);
      if (created_at) {
        formData.append('created_at', created_at);
      }

      const response = await api.post(`/projects/${projectUuid}/offers`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
  ,

  // Teklif sil
  async deleteOffer(offerId) {
    try {
      const response = await api.delete(`/projects/offers/${offerId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
  ,

  // Teklif güncelle
  async updateOffer(offerId, formData) {
    try {
      const response = await api.put(`/projects/offers/${offerId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Teklif durumu güncelle
  async updateOfferStatus(offerId, status) {
    try {
      const response = await api.put(`/projects/offers/${offerId}/status`, { status });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Teklif dosyası sil
  async deleteProjectOfferFile(offerId) {
    try {
      const response = await api.delete(`/projects/offers/${offerId}/file`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Teklif dosyası indir
  async downloadProjectOfferFile(offerId) {
    try {
      const response = await api.get(`/projects/offers/${offerId}/download`, {
        responseType: 'blob'
      });
      return response;
    } catch (error) {
      throw error;
    }
  }
};
